import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useModal } from '../contexts/ModalContext';
import EmptyState from '../components/EmptyState';
import { useClientsData, queryKeys } from '../hooks/useData';
import InviteClientModal from '../components/InviteClientModal';
import { useToast } from '../contexts/ToastContext';
import { clientsApi, clientPortalAdminApi } from '../utils/api';
import {
  UserIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  EnvelopeIcon,
  EyeIcon,
  PencilSquareIcon,
  PaperAirplaneIcon,
  ClipboardDocumentIcon,
  DocumentTextIcon,
  BriefcaseIcon,
  MapPinIcon,
  TrashIcon,
  NoSymbolIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { Search as SearchIcon, Plus as PlusIcon } from 'lucide-react';
import {
  Badge,
  Button,
  DataTable,
  DataTableColumn,
  PageHeader,
  RowAction,
} from '../components/ui';

interface ClientRow {
  id: string;
  name: string;
  companyName?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  postcode?: string | null;
  status?: string;
  createdAt?: string;
  lastJobAt?: string | null;
  totalJobs?: number;
  totalSpendPence?: number;
  portalStatus?: 'none' | 'pending' | 'active';
}

const formatDate = (dateString?: string | null) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatMoney = (pence?: number) => {
  if (pence == null) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(pence / 100);
};

export default function Clients() {
  const { openModal } = useModal();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const { data: allClients = [], isLoading: loading, error } = useClientsData();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [inviteTarget, setInviteTarget] = useState<{ id: string; name: string; email?: string | null } | null>(null);
  const itemsPerPage = 10;

  const filteredClients = useMemo<ClientRow[]>(() => {
    let out: ClientRow[] = [...(allClients as ClientRow[])];
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      out = out.filter(
        (c) =>
          c.name?.toLowerCase().includes(q) ||
          c.companyName?.toLowerCase().includes(q) ||
          c.email?.toLowerCase().includes(q) ||
          c.phone?.includes(searchTerm),
      );
    }
    if (statusFilter) {
      out = out.filter((c) => (c.status || 'active').toLowerCase() === statusFilter.toLowerCase());
    }
    return out;
  }, [allClients, searchTerm, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredClients.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginated = filteredClients.slice(startIndex, startIndex + itemsPerPage);

  const refreshClients = () => queryClient.invalidateQueries({ queryKey: queryKeys.clients });

  const handleInvite = (client: ClientRow) => {
    if (!client.email) {
      showToast('This client has no email address on file. Add one first.', 'error');
      return;
    }
    setInviteTarget({ id: client.id, name: client.name, email: client.email });
  };

  const handleResendInvite = async (client: ClientRow) => {
    try {
      await clientPortalAdminApi.resend(client.id);
      showToast('Portal invite resent', 'success');
    } catch (err: any) {
      showToast(err?.response?.data?.message || 'Could not resend invite', 'error');
    }
  };

  const handleRevokeInvite = async (client: ClientRow) => {
    if (!window.confirm(`Revoke portal access for ${client.name}?`)) return;
    try {
      await clientPortalAdminApi.revoke(client.id);
      showToast('Portal access revoked', 'success');
      refreshClients();
    } catch (err: any) {
      showToast(err?.response?.data?.message || 'Could not revoke', 'error');
    }
  };

  const handleCopyEmail = async (client: ClientRow) => {
    if (!client.email) {
      showToast('No email on file', 'error');
      return;
    }
    try {
      await navigator.clipboard.writeText(client.email);
      showToast('Email copied to clipboard', 'success');
    } catch {
      showToast('Could not copy', 'error');
    }
  };

  const handleDelete = async (client: ClientRow) => {
    if (
      !window.confirm(
        `Delete ${client.name}? This will only succeed if they have no jobs, quotes, or certificates.`,
      )
    ) {
      return;
    }
    try {
      await clientsApi.delete(client.id);
      showToast(`${client.name} deleted`, 'success');
      refreshClients();
    } catch (err: any) {
      showToast(
        err?.response?.data?.message ||
          'Could not delete client — they may still have related records.',
        'error',
      );
    }
  };

  const buildRowActions = (client: ClientRow): RowAction[] => {
    const portalActive = client.portalStatus === 'active';
    const portalPending = client.portalStatus === 'pending';

    return [
      {
        id: 'view',
        label: 'View client',
        icon: EyeIcon,
        onClick: () => navigate(`/dashboard/clients/${client.id}`),
      },
      {
        id: 'edit',
        label: 'Edit details',
        icon: PencilSquareIcon,
        onClick: () => openModal('editClient', { client }),
      },
      {
        id: 'copy-email',
        label: 'Copy email',
        icon: ClipboardDocumentIcon,
        onClick: () => handleCopyEmail(client),
        disabled: !client.email,
      },
      {
        id: 'portal-invite',
        label: portalPending ? 'Resend portal invite' : 'Invite to portal',
        description: portalActive ? 'Already active' : undefined,
        icon: PaperAirplaneIcon,
        divider: true,
        hidden: portalActive,
        onClick: () => (portalPending ? handleResendInvite(client) : handleInvite(client)),
        disabled: !client.email,
      },
      {
        id: 'portal-revoke',
        label: 'Revoke portal access',
        icon: NoSymbolIcon,
        hidden: !portalActive && !portalPending,
        danger: true,
        onClick: () => handleRevokeInvite(client),
      },
      {
        id: 'create-quote',
        label: 'Create quote',
        icon: DocumentTextIcon,
        divider: true,
        onClick: () => openModal('createQuote', { initialData: { customerId: client.id, customerName: client.name } }),
      },
      {
        id: 'create-job',
        label: 'Create job',
        icon: BriefcaseIcon,
        onClick: () => openModal('createJob', { initialData: { customerId: client.id, customerName: client.name } }),
      },
      {
        id: 'add-site',
        label: 'Add site for this client',
        icon: MapPinIcon,
        onClick: () => openModal('createSite', { initialData: { clientId: client.id } }),
      },
      {
        id: 'delete',
        label: 'Delete client',
        icon: TrashIcon,
        danger: true,
        divider: true,
        onClick: () => handleDelete(client),
      },
    ];
  };

  const columns: DataTableColumn<ClientRow>[] = [
    {
      id: 'client',
      header: 'Client',
      render: (c) => (
        <div className="ui-client-cell">
          <div className="ui-client-avatar">
            <UserIcon className="w-4 h-4" />
          </div>
          <div className="ui-client-identity">
            <div className="ui-client-name">{c.name || 'Unnamed'}</div>
            <div className="ui-client-secondary">
              {c.companyName ? (
                <span className="ui-client-company">
                  <BuildingOfficeIcon className="w-3 h-3" />
                  {c.companyName}
                </span>
              ) : (
                <span className="ui-client-muted">Individual</span>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'contact',
      header: 'Contact',
      hideBelow: 'md',
      render: (c) => (
        <div className="ui-stacked-contact">
          <div className="ui-stacked-row">
            <EnvelopeIcon className="w-3.5 h-3.5" />
            <span>{c.email || '—'}</span>
          </div>
          <div className="ui-stacked-row">
            <PhoneIcon className="w-3.5 h-3.5" />
            <span>{c.phone || '—'}</span>
          </div>
        </div>
      ),
    },
    {
      id: 'address',
      header: 'Address',
      hideBelow: 'lg',
      render: (c) => (
        <span className="ui-cell-muted">
          {[c.address, c.postcode].filter(Boolean).join(', ') || '—'}
        </span>
      ),
    },
    {
      id: 'spending',
      header: 'Jobs · Spend',
      align: 'right',
      hideBelow: 'md',
      render: (c) => (
        <div className="ui-cell-financial">
          <div className="ui-cell-primary">{c.totalJobs ?? 0} jobs</div>
          <div className="ui-cell-muted">{formatMoney(c.totalSpendPence)}</div>
        </div>
      ),
    },
    {
      id: 'lastJob',
      header: 'Last job',
      hideBelow: 'lg',
      render: (c) => <span className="ui-cell-muted">{formatDate(c.lastJobAt || c.createdAt)}</span>,
    },
    {
      id: 'status',
      header: 'Status',
      render: (c) => {
        const status = (c.status || 'active').toLowerCase();
        const tone = status === 'inactive' ? 'neutral' : 'success';
        const portal = c.portalStatus;
        return (
          <div className="ui-status-stack">
            <Badge tone={tone} dot>
              {status === 'inactive' ? 'Inactive' : 'Active'}
            </Badge>
            {portal === 'active' && (
              <Badge tone="info">Portal</Badge>
            )}
            {portal === 'pending' && (
              <Badge tone="warning">Invite sent</Badge>
            )}
          </div>
        );
      },
    },
  ];

  if (error) {
    return (
      <div className="page-container">
        <PageHeader
          title="Clients"
          subtitle="Manage customer relationships and contact information."
          actions={<Button onClick={() => window.location.reload()}>Try again</Button>}
        />
        <div className="ui-error-card">
          <h3>Could not load clients</h3>
          <p>{error?.message || 'An unexpected error occurred.'}</p>
        </div>
      </div>
    );
  }

  if (!loading && allClients.length === 0) {
    return (
      <div className="page-container">
        <PageHeader
          title="Clients"
          subtitle="Manage customer relationships and contact information."
          actions={
            <Button leftIcon={<PlusIcon className="w-4 h-4" />} onClick={() => openModal('createClient')}>
              Add client
            </Button>
          }
        />
        <EmptyState
          icon={UserIcon}
          title="No clients yet"
          description="Add your first client to start managing customer relationships and service history."
          actionLabel="Add client"
          onAction={() => openModal('createClient')}
        />
      </div>
    );
  }

  return (
    <div className="page-container">
      <PageHeader
        title="Clients"
        subtitle="Manage customer relationships, contact details, and service history in one place."
        search={
          <div className="ui-inline-search">
            <SearchIcon className="ui-inline-search-icon" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search by name, company, email, phone…"
              className="ui-inline-search-input"
            />
          </div>
        }
        actions={
          <>
            <select
              className="ui-input ui-select ui-filter-inline"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All clients</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <Button
              variant="primary"
              leftIcon={<PlusIcon className="w-4 h-4" />}
              onClick={() => openModal('createClient')}
            >
              Add client
            </Button>
          </>
        }
      />

      <DataTable<ClientRow>
        columns={columns}
        rows={paginated}
        getRowKey={(c) => c.id}
        loading={loading}
        onRowClick={(c) => navigate(`/dashboard/clients/${c.id}`)}
        rowActions={buildRowActions}
        empty={
          <div className="ui-table-empty-default">
            <p>No clients match your filters.</p>
            {(searchTerm || statusFilter) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('');
                }}
              >
                Clear filters
              </Button>
            )}
          </div>
        }
      />

      {totalPages > 1 && (
        <div className="ui-pagination">
          <div className="ui-pagination-info">
            Page {currentPage} of {totalPages} · {filteredClients.length} clients
            {(searchTerm || statusFilter) && ` (filtered from ${allClients.length})`}
          </div>
          <div className="ui-pagination-controls">
            <Button
              variant="ghost"
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              leftIcon={<ChevronLeftIcon className="w-4 h-4" />}
            >
              Previous
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              rightIcon={<ChevronRightIcon className="w-4 h-4" />}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <InviteClientModal
        open={!!inviteTarget}
        client={inviteTarget}
        onClose={() => setInviteTarget(null)}
        onSent={refreshClients}
        showToast={showToast}
      />
    </div>
  );
}
